<script>
    export let data = [];
    export let searchedTimeSlots = [];
    export let availabilityWindow = null;

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const toMinutes = value => value.split(':').map(Number).reduce((hours, minutes) => hours * 60 + minutes);
    const toTime = minutes => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

    function merge(ranges) {
        return ranges.map(range => [...range]).sort((a, b) => a[0] - b[0]).reduce((result, range) => {
            const last = result[result.length - 1];
            if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
            else result.push(range);
            return result;
        }, []);
    }

    function subtract(available, conflicts) {
        let result = merge(available);
        for (const [conflictStart, conflictEnd] of merge(conflicts)) {
            result = result.flatMap(([start, end]) => {
                if (conflictEnd <= start || conflictStart >= end) return [[start, end]];
                const pieces = [];
                if (conflictStart > start) pieces.push([start, conflictStart]);
                if (conflictEnd < end) pieces.push([conflictEnd, end]);
                return pieces;
            });
        }
        return result;
    }

    function dayDetails(teacher, day) {
        const availability = (teacher.matched_timeslots || []).filter(slot => Number(slot.day_of_week) === day).map(slot => [toMinutes(slot.start_time), toMinutes(slot.end_time)]);
        const bookings = (teacher.conflicting_classes || []).filter(item => Number(item.day_of_week) === day).map(item => [toMinutes(item.class_start_time), toMinutes(item.class_end_time)]);
        const preferences = searchedTimeSlots.filter(slot => Number(slot.day_of_week) === day).map(slot => [toMinutes(slot.start_time), toMinutes(slot.end_time)]);
        return {
            free: subtract(availability, bookings),
            booked: merge(bookings.flatMap(([start, end]) => availability
                .map(([availableStart, availableEnd]) => [Math.max(start, availableStart), Math.min(end, availableEnd)])
                .filter(([overlapStart, overlapEnd]) => overlapStart < overlapEnd))),
            preferred: merge(preferences)
        };
    }

    function formatDuration(minutes) {
        if (!minutes) return '0 min';
        const hours = Math.floor(minutes / 60);
        const remainder = minutes % 60;
        return [hours ? `${hours}h` : '', remainder ? `${remainder}m` : ''].filter(Boolean).join(' ');
    }
</script>

<section class="results" aria-live="polite">
    <div class="results-heading">
        <div>
            <h3>{data.length} matching {data.length === 1 ? 'teacher' : 'teachers'}</h3>
            {#if availabilityWindow}<p>Bookings checked {availabilityWindow.start_date} to {availabilityWindow.end_date} ({availabilityWindow.timezone}).</p>{/if}
        </div>
        <div class="legend" aria-label="Availability color legend">
            <span><i class="free-dot"></i> Free</span>
            <span><i class="booked-dot"></i> Booked</span>
            {#if searchedTimeSlots.length}<span><i class="preferred-dot"></i> Preferred</span>{/if}
        </div>
    </div>

    {#if data.length === 0}
        <div class="empty">No active teachers teach this subject at the selected grade.</div>
    {:else}
        {#each data as teacher, index (teacher.teacher_id)}
            <article class="teacher-card">
                <header>
                    <div class="identity">
                        <span class="rank" aria-label="Result rank {index + 1}">{index + 1}</span>
                        <div><h4>{teacher.teacher_name}</h4><p>{teacher.subject} · Grades {teacher.grade}</p></div>
                    </div>
                    <div class="summary">
                        {#if searchedTimeSlots.length}
                            <strong>{formatDuration(teacher.preferred_overlap_minutes)}</strong><span>free in preferred times</span>
                        {:else}
                            <strong>{formatDuration(teacher.total_free_minutes)}</strong><span>total free time</span>
                        {/if}
                    </div>
                </header>

                {#if !teacher.has_submitted_timeslots}
                    <div class="warning" role="status">No availability has been submitted by this teacher.</div>
                {:else}
                    <div class="week">
                        {#each days as dayName, dayIndex}
                            {@const details = dayDetails(teacher, dayIndex + 1)}
                            {#if details.free.length || details.booked.length || details.preferred.length}
                                <div class="day-row">
                                    <div class="day-name">{dayName}</div>
                                    <div class="day-times">
                                        {#each details.preferred as range}<span class="time preferred"><b>Preferred</b> {toTime(range[0])}–{toTime(range[1])}</span>{/each}
                                        {#each details.free as range}<span class="time free"><b>Free</b> {toTime(range[0])}–{toTime(range[1])}</span>{/each}
                                        {#each details.booked as range}<span class="time booked"><b>Booked</b> {toTime(range[0])}–{toTime(range[1])}</span>{/each}
                                        {#if !details.free.length && !details.booked.length}<span class="no-time">No submitted availability</span>{/if}
                                    </div>
                                </div>
                            {/if}
                        {/each}
                    </div>
                    {#if searchedTimeSlots.length && teacher.preferred_overlap_minutes === 0}
                        <p class="no-overlap">No free time overlaps the preferred ranges.</p>
                    {/if}
                {/if}
            </article>
        {/each}
    {/if}
</section>

<style>
    .results { margin-top: 1.75rem; color: #1f2937; }
    .results-heading { display: flex; justify-content: space-between; align-items: end; gap: 1rem; margin-bottom: 1rem; }
    .results-heading h3 { margin: 0; font-size: 1.3rem; }
    .results-heading p { margin: .3rem 0 0; color: #64748b; font-size: .88rem; }
    .legend { display: flex; flex-wrap: wrap; gap: .8rem; font-size: .84rem; color: #475569; }
    .legend span { display: inline-flex; align-items: center; gap: .35rem; }
    .legend i { width: .75rem; height: .75rem; border-radius: 3px; }
    .free-dot { background: #22c55e; } .booked-dot { background: #ef4444; } .preferred-dot { background: #3b82f6; }
    .empty, .warning { padding: 1rem; border-radius: 9px; background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412; }
    .teacher-card { margin-bottom: 1rem; overflow: hidden; border: 1px solid #dbe3ec; border-radius: 12px; background: white; box-shadow: 0 2px 8px rgba(15, 23, 42, .06); }
    .teacher-card header { display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: 1rem 1.25rem; background: #fff8ed; border-bottom: 1px solid #fde4bd; }
    .identity { display: flex; align-items: center; gap: .8rem; }
    .rank { display: grid; place-items: center; min-width: 2rem; height: 2rem; border-radius: 50%; background: #fab45a; font-weight: 750; }
    .identity h4 { margin: 0; font-size: 1.15rem; } .identity p { margin: .2rem 0 0; color: #64748b; }
    .summary { display: flex; flex-direction: column; align-items: end; text-align: right; }
    .summary strong { color: #166534; font-size: 1.15rem; } .summary span { color: #64748b; font-size: .78rem; }
    .week { padding: .5rem 1.25rem 1rem; }
    .day-row { display: grid; grid-template-columns: 110px 1fr; gap: .75rem; padding: .75rem 0; border-bottom: 1px solid #edf1f5; }
    .day-row:last-child { border-bottom: 0; } .day-name { padding-top: .35rem; font-weight: 700; }
    .day-times { display: flex; flex-wrap: wrap; gap: .45rem; }
    .time { padding: .38rem .6rem; border: 1px solid; border-radius: 7px; font-size: .85rem; white-space: nowrap; }
    .time b { margin-right: .25rem; font-size: .7rem; text-transform: uppercase; }
    .free { color: #166534; background: #dcfce7; border-color: #86efac; }
    .booked { color: #991b1b; background: #fee2e2; border-color: #fca5a5; }
    .preferred { color: #1e40af; background: #dbeafe; border-color: #93c5fd; }
    .warning { margin: 1rem 1.25rem; }
    .no-overlap { margin: 0 1.25rem 1rem; padding: .65rem .8rem; border-radius: 7px; color: #475569; background: #f1f5f9; }
    .no-time { padding: .35rem .55rem; color: #64748b; font-style: italic; }
    @media (max-width: 700px) {
        .results-heading, .teacher-card header { align-items: stretch; flex-direction: column; }
        .summary { align-items: start; text-align: left; }
        .day-row { grid-template-columns: 1fr; gap: .35rem; }
    }
</style>
